import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, Download, X, FileText, Copy, Receipt, Camera, ClipboardList, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { API, BACKEND_URL, statusColors, formatDate, formatCurrency } from "./utils";
import { VariationsContent } from "./VariationsContent";
import { useFormDraft } from "@/hooks/useFormDraft";
import { DraftRestoredBanner } from "@/components/DraftRestoredBanner";
import { DraftIndicatorBadge } from "@/components/DraftIndicatorBadge";

function BOQContent({ projectId, boqs, quotations, onRefresh, canWrite, token }) {
  const [activeTab, setActiveTab] = useState("quotations");
  const [showQuotationForm, setShowQuotationForm] = useState(false);
  // Use quotations prop directly instead of fetching separately to avoid duplicate API calls
  const [projectQuotations, setProjectQuotations] = useState(quotations || []);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  // Default scope of work items
  const defaultScopeItems = [
    "Design coordination",
    "Turnkey execution (raw shell to handover)",
    "Civil, Electrical & MEP works",
    "Furniture & fixtures",
    "Vendor & site management",
    "Multi-city execution support"
  ];

  // Default cost categories
  const defaultCostCategories = [
    { category: "Civil Works", description: "As per approved drawings", amount: 0 },
    { category: "Electrical Works", description: "Wiring, lighting, panels", amount: 0 },
    { category: "Plumbing / HVAC", description: "If applicable", amount: 0 },
    { category: "Furniture & Fixtures", description: "Custom & modular", amount: 0 },
    { category: "Branding & Signage", description: "As per brand guidelines", amount: 0 }
  ];


  // Default payment milestones
  const defaultMilestones = [
    { name: "Work Order Confirmation", percentage: 10 },
    { name: "Design Finalisation", percentage: 20 },
    { name: "Mid-Execution", percentage: 30 },
    { name: "Near Completion", percentage: 30 },
    { name: "Final Handover", percentage: 10 }
  ];

  // Default exclusions
  const defaultExclusions = [
    "Government approvals & statutory fees",
    "Client-supplied materials",
    "Any scope not mentioned above"
  ];

  // Default terms
  const defaultTerms = [
    "Quotation validity: 15 days",
    "Any variation in scope will be charged additionally",
    "Payment delays may impact timelines",
    "Material prices subject to market fluctuation"
  ];

  const getDefaultFormData = () => ({
    client_name: "",
    project_name: "",
    site_address: "",
    project_type: "Commercial",
    scope_of_work: defaultScopeItems,
    items: [],  // New: Individual items with images
    cost_breakup: defaultCostCategories,
    payment_milestones: defaultMilestones,
    project_start_days: 7,
    execution_duration_days: 45,
    exclusions: defaultExclusions,
    terms_conditions: defaultTerms,
    gst_percent: 18,
    gst_exempt: false,
    validity_days: 15,
    notes: "",
    manual_id: "",
    status: "Draft"  // New: Draft, Pending, In-Progress, Approved
  });

  const [formData, setFormData, draft] = useFormDraft(
    `newQuotation:${projectId}`,
    getDefaultFormData(),
    { enabled: showQuotationForm && !editingQuotation }
  );
  const [draftRestored, setDraftRestored] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Status options
  const statusOptions = ["Draft", "Pending", "In-Progress", "Approved"];

  // Sync with parent quotations prop when it changes
  useEffect(() => {
    if (quotations && quotations.length >= 0) {
      setProjectQuotations(quotations);
    }
  }, [quotations]);

  // Fetch quotations only when needed (after create/update/delete)
  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quotations/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectQuotations(res.data);
    } catch (error) {
      console.error("Failed to fetch quotations", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitQuotation = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingQuotation) {
        // Update existing quotation
        await axios.put(`${API}/quotations/${editingQuotation.id}`, {
          ...formData
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Quotation updated successfully!");
      } else {
        // Create new quotation
        await axios.post(`${API}/quotations`, {
          project_id: projectId,
          ...formData,
          manual_id: formData.manual_id || null  // Include manual ID
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Quotation created successfully!");
        draft.clear();
      }
      setShowQuotationForm(false);
      setEditingQuotation(null);
      setDraftRestored(false);
      fetchQuotations();
      setFormData(getDefaultFormData());
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save quotation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditQuotation = async (quotation) => {
    // Fetch full quotation details
    try {
      const res = await axios.get(`${API}/quotations/${quotation.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const qt = res.data;
      setFormData({
        client_name: qt.client_name || "",
        project_name: qt.project_name || "",
        site_address: qt.site_address || "",
        project_type: qt.project_type || "Commercial",
        scope_of_work: qt.scope_of_work || defaultScopeItems,
        items: qt.items || [],  // Include items
        cost_breakup: qt.cost_breakup || defaultCostCategories,
        payment_milestones: qt.payment_milestones || defaultMilestones,
        project_start_days: qt.project_start_days || 7,
        execution_duration_days: qt.execution_duration_days || 45,
        exclusions: qt.exclusions || defaultExclusions,
        terms_conditions: qt.terms_conditions || defaultTerms,
        gst_percent: qt.gst_percent || 18,
        gst_exempt: qt.gst_exempt || false,
        validity_days: qt.validity_days || 15,
        notes: qt.notes || "",
        manual_id: qt.manual_id || "",
        status: qt.status || "Draft"
      });
      setEditingQuotation(quotation);
      setShowQuotationForm(true);
    } catch (error) {
      toast.error("Failed to load quotation details");
    }
  };

  const handleCancelForm = () => {
    setShowQuotationForm(false);
    setEditingQuotation(null);
    setDraftRestored(false);
    setFormData(getDefaultFormData());
  };

  const openNewQuotationForm = () => {
    const restored = draft.restoreIfAny();
    setDraftRestored(restored);
    setShowQuotationForm(true);
  };

  const handleDownloadPDF = async (quotationId) => {
    if (downloadingPdf) return; // Prevent double-clicks
    setDownloadingPdf(quotationId);
    try {
      const response = await axios.get(`${API}/quotations/${quotationId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 60000 // 60 second timeout for PDF generation
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quotation_${quotationId}.pdf`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF download error:", error);
      toast.error("Failed to download PDF. Please try again.");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleDeleteQuotation = async (quotationId) => {
    if (!window.confirm("Are you sure you want to delete this quotation?")) return;
    try {
      await axios.delete(`${API}/quotations/${quotationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quotation deleted");
      fetchQuotations();
    } catch (error) {
      toast.error("Failed to delete quotation");
    }
  };

  const updateCostItem = (index, field, value) => {
    const newCostBreakup = [...formData.cost_breakup];
    newCostBreakup[index] = { ...newCostBreakup[index], [field]: field === 'amount' ? parseFloat(value) || 0 : value };
    setFormData({ ...formData, cost_breakup: newCostBreakup });
  };

  const updateMilestone = (index, field, value) => {
    const newMilestones = [...formData.payment_milestones];
    newMilestones[index] = { ...newMilestones[index], [field]: field === 'percentage' ? parseFloat(value) || 0 : value };
    setFormData({ ...formData, payment_milestones: newMilestones });
  };

  const addCostItem = () => {
    setFormData({
      ...formData,
      cost_breakup: [...formData.cost_breakup, { category: "", description: "", amount: 0 }]
    });
  };

  const removeCostItem = (index) => {
    const newCostBreakup = formData.cost_breakup.filter((_, i) => i !== index);
    setFormData({ ...formData, cost_breakup: newCostBreakup });
  };

  // Scope of Work helpers
  const addScopeItem = () => {
    setFormData({ ...formData, scope_of_work: [...formData.scope_of_work, ""] });
  };
  const updateScopeItem = (index, value) => {
    const newScope = [...formData.scope_of_work];
    newScope[index] = value;
    setFormData({ ...formData, scope_of_work: newScope });
  };
  const removeScopeItem = (index) => {
    setFormData({ ...formData, scope_of_work: formData.scope_of_work.filter((_, i) => i !== index) });
  };

  // Exclusions helpers
  const addExclusionItem = () => {
    setFormData({ ...formData, exclusions: [...formData.exclusions, ""] });
  };
  const updateExclusionItem = (index, value) => {
    const newExclusions = [...formData.exclusions];
    newExclusions[index] = value;
    setFormData({ ...formData, exclusions: newExclusions });
  };
  const removeExclusionItem = (index) => {
    setFormData({ ...formData, exclusions: formData.exclusions.filter((_, i) => i !== index) });
  };

  // Terms & Conditions helpers
  const addTermItem = () => {
    setFormData({ ...formData, terms_conditions: [...formData.terms_conditions, ""] });
  };
  const updateTermItem = (index, value) => {
    const newTerms = [...formData.terms_conditions];
    newTerms[index] = value;
    setFormData({ ...formData, terms_conditions: newTerms });
  };
  const removeTermItem = (index) => {
    setFormData({ ...formData, terms_conditions: formData.terms_conditions.filter((_, i) => i !== index) });
  };

  // Items helpers (new format with images)
  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        category: "",
        sub_category: "",
        item_name: "",
        description: "",
        specification: "",
        image_url: "",
        uom: "Nos",
        unit_price: 0,
        quantity: 1,
        total_price: 0
      }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    // Auto-calculate total_price
    if (field === 'unit_price' || field === 'quantity') {
      const unitPrice = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(newItems[index].unit_price) || 0;
      const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(newItems[index].quantity) || 0;
      newItems[index].total_price = unitPrice * qty;
    }
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    
    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('category', 'quotation_image');
    
    try {
      const response = await axios.post(`${API}/projects/${projectId}/documents`, uploadFormData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // The backend returns file_url as "/api/projects/files/{filename}"
      const doc = response.data.document || response.data;
      const fileUrl = doc.file_url;
      // Construct full URL for image - BACKEND_URL already includes the base
      const imageUrl = `${BACKEND_URL}${fileUrl}`;
      console.log("Image URL:", imageUrl);
      updateItem(index, 'image_url', imageUrl);
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  // Calculate subtotal from items (new) or cost_breakup (old)
  const itemsSubtotal = formData.items.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
  const costBreakupSubtotal = formData.cost_breakup.reduce((sum, item) => sum + (item.amount || 0), 0);
  const subtotal = formData.items.length > 0 ? itemsSubtotal : costBreakupSubtotal;
  const gstAmount = formData.gst_exempt ? 0 : subtotal * formData.gst_percent / 100;
  const total = subtotal + gstAmount;

  // Update status of a quotation
  const handleStatusUpdate = async (quotationId, newStatus) => {
    try {
      await axios.put(`${API}/quotations/${quotationId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${newStatus}`);
      fetchQuotations();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const statusColors = {
    draft: "bg-slate-100 text-slate-700",
    Draft: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
    Pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    "In-Progress": "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    Approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    sent: "bg-blue-100 text-blue-700",
    accepted: "bg-emerald-100 text-emerald-700",
    rejected: "bg-red-100 text-red-700"
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="quotations">Quotations</TabsTrigger>
          <TabsTrigger value="boq">Bill of Quantities</TabsTrigger>
          <TabsTrigger value="variations" data-testid="tab-variations">Variations</TabsTrigger>
        </TabsList>

        <TabsContent value="quotations">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-slate-900">Project Quotations</h4>
              {canWrite && (
                <div className="flex items-center gap-3">
                  <DraftIndicatorBadge
                    storageKey={`newQuotation:${projectId}`}
                    onResume={openNewQuotationForm}
                  />
                  <Button 
                    size="sm" 
                    onClick={openNewQuotationForm} 
                    className="bg-brillien-600 hover:bg-brillien-700"
                    data-testid="create-quotation-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />Create Quotation
                  </Button>
                </div>
              )}
            </div>

            {/* Quotation Form */}
            {showQuotationForm && (
              <Card className="p-6 border-brillien-200 bg-brillien-50/50" data-testid="quotation-form">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-slate-900 text-lg">
                    {editingQuotation ? `Edit Quotation - ${editingQuotation.quotation_number}` : "Create New Quotation"}
                  </h4>
                  <Button variant="ghost" size="sm" onClick={handleCancelForm}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <form onSubmit={handleSubmitQuotation} className="space-y-6">
                  {!editingQuotation && draftRestored && (
                    <DraftRestoredBanner
                      onDiscard={() => {
                        draft.clear();
                        setFormData(getDefaultFormData());
                        setDraftRestored(false);
                      }}
                    />
                  )}
                  {/* Manual ID field - only show when creating new quotation */}
                  {!editingQuotation && (
                    <div className="bg-slate-50 dark:bg-slate-700/50 p-3 rounded-lg border border-dashed border-slate-300 dark:border-slate-400">
                      <Label className="text-sm font-medium">
                        Quotation ID <span className="text-slate-400 font-normal">(Optional - leave blank for auto-generated ID)</span>
                      </Label>
                      <Input 
                        value={formData.manual_id || ""} 
                        onChange={(e) => setFormData({...formData, manual_id: e.target.value})}
                        placeholder="e.g., QTN-CUSTOM-001"
                        className="mt-1"
                        data-testid="quotation-manual-id"
                      />
                    </div>
                  )}
                  
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Client Name *</Label>
                      <Input 
                        value={formData.client_name} 
                        onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                        required
                        data-testid="quotation-client-name"
                      />
                    </div>
                    <div>
                      <Label>Project Name *</Label>
                      <Input 
                        value={formData.project_name} 
                        onChange={(e) => setFormData({...formData, project_name: e.target.value})}
                        required
                        data-testid="quotation-project-name"
                      />
                    </div>
                    <div>
                      <Label>Site Address (City, State) *</Label>
                      <Input 
                        value={formData.site_address} 
                        onChange={(e) => setFormData({...formData, site_address: e.target.value})}
                        required
                        data-testid="quotation-site-address"
                      />
                    </div>
                    <div>
                      <Label>Project Type</Label>
                      <Select value={formData.project_type} onValueChange={(val) => setFormData({...formData, project_type: val})}>
                        <SelectTrigger data-testid="quotation-project-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Commercial">Commercial</SelectItem>
                          <SelectItem value="Residential">Residential</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Scope of Work */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-base font-semibold">Scope of Work</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addScopeItem}>
                        <Plus className="h-3 w-3 mr-1" />Add Item
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {formData.scope_of_work.map((item, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <span className="text-slate-400 text-sm w-6">•</span>
                          <Input 
                            value={item} 
                            onChange={(e) => updateScopeItem(idx, e.target.value)}
                            placeholder="Enter scope item..."
                            className="flex-1"
                          />
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeScopeItem(idx)}
                            className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Items with Images (New Format) */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-base font-semibold">Quotation Items (with Images)</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addItem}>
                        <Plus className="h-3 w-3 mr-1" />Add Item
                      </Button>
                    </div>
                    {formData.items.length === 0 ? (
                      <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                        <ImageIcon className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-slate-500 text-sm">No items added yet. Click "Add Item" to add quotation items with images.</p>
                        <p className="text-slate-400 text-xs mt-1">Or use the "Category-wise Cost Breakup" section below for simple quotations.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {formData.items.map((item, idx) => (
                          <Card key={idx} className="p-4 border-slate-200 bg-white">
                            <div className="flex items-start justify-between mb-3">
                              <span className="text-sm font-medium text-slate-500">Item #{idx + 1}</span>
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => removeItem(idx)}
                                className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-3 mb-3">
                              <div>
                                <Label className="text-xs">Category *</Label>
                                <Input 
                                  value={item.category} 
                                  onChange={(e) => updateItem(idx, 'category', e.target.value)}
                                  placeholder="e.g., Furniture"
                                  className="h-9"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Sub-Category</Label>
                                <Input 
                                  value={item.sub_category} 
                                  onChange={(e) => updateItem(idx, 'sub_category', e.target.value)}
                                  placeholder="e.g., Office Chairs"
                                  className="h-9"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Item Name *</Label>
                                <Input 
                                  value={item.item_name} 
                                  onChange={(e) => updateItem(idx, 'item_name', e.target.value)}
                                  placeholder="e.g., Executive Chair"
                                  className="h-9"
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              <div>
                                <Label className="text-xs">Description *</Label>
                                <Textarea 
                                  value={item.description} 
                                  onChange={(e) => updateItem(idx, 'description', e.target.value)}
                                  placeholder="Detailed description of the item..."
                                  rows={2}
                                  className="text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Specification</Label>
                                <Textarea 
                                  value={item.specification} 
                                  onChange={(e) => updateItem(idx, 'specification', e.target.value)}
                                  placeholder="Size, material, brand, etc."
                                  rows={2}
                                  className="text-sm"
                                />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-6 gap-3 mb-3">
                              <div>
                                <Label className="text-xs">UOM</Label>
                                <Select value={item.uom} onValueChange={(val) => updateItem(idx, 'uom', val)}>
                                  <SelectTrigger className="h-9">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Nos">Nos</SelectItem>
                                    <SelectItem value="Sqft">Sqft</SelectItem>
                                    <SelectItem value="Rft">Rft</SelectItem>
                                    <SelectItem value="Set">Set</SelectItem>
                                    <SelectItem value="Lump">Lump</SelectItem>
                                    <SelectItem value="Kg">Kg</SelectItem>
                                    <SelectItem value="Mtr">Mtr</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-xs">Unit Price (₹)</Label>
                                <Input 
                                  type="number" 
                                  value={item.unit_price || ''} 
                                  onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                                  placeholder="0"
                                  className="h-9"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Quantity</Label>
                                <Input 
                                  type="number" 
                                  value={item.quantity || ''} 
                                  onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                  placeholder="1"
                                  className="h-9"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Total Price (₹)</Label>
                                <Input 
                                  type="number" 
                                  value={item.total_price || ''} 
                                  disabled
                                  className="h-9 bg-slate-100"
                                />
                              </div>
                              <div className="col-span-2">
                                <Label className="text-xs">Image</Label>
                                <div className="flex gap-2 items-center">
                                  {item.image_url ? (
                                    <div className="relative">
                                      <img 
                                        src={item.image_url} 
                                        alt="" 
                                        className="h-12 w-12 object-cover rounded border"
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="gray" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>';
                                        }}
                                      />
                                      <button 
                                        type="button"
                                        onClick={() => updateItem(idx, 'image_url', '')}
                                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center text-xs"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ) : (
                                    <label className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded cursor-pointer text-sm">
                                      <Upload className="h-4 w-4" />
                                      {uploading ? 'Uploading...' : 'Upload'}
                                      <input 
                                        type="file" 
                                        accept="image/*" 
                                        className="hidden" 
                                        onChange={(e) => handleImageUpload(idx, e.target.files[0])}
                                        disabled={uploading}
                                      />
                                    </label>
                                  )}
                                  <Input 
                                    value={item.image_url || ''} 
                                    onChange={(e) => updateItem(idx, 'image_url', e.target.value)}
                                    placeholder="Or paste image URL"
                                    className="h-9 flex-1 text-xs"
                                  />
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* Category-wise Summary (auto-calculated from items) */}
                    {formData.items.length > 0 && (
                      <div className="mt-4 bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <Label className="text-sm font-semibold mb-2 block text-slate-700">Category-wise Cost Summary (Auto-calculated)</Label>
                        <div className="space-y-1">
                          {Object.entries(
                            formData.items.reduce((acc, item) => {
                              const cat = item.category || 'Uncategorized';
                              acc[cat] = (acc[cat] || 0) + (parseFloat(item.total_price) || 0);
                              return acc;
                            }, {})
                          ).map(([category, total], idx) => (
                            <div key={idx} className="flex justify-between text-sm py-1 border-b border-slate-200 last:border-0">
                              <span className="text-slate-600">{category}</span>
                              <span className="font-mono font-medium text-slate-800">₹{total.toLocaleString('en-IN', {minimumFractionDigits: 0})}</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm pt-2 font-semibold text-brillien-600 border-t border-brillien-200">
                            <span>Items Total</span>
                            <span className="font-mono">₹{itemsSubtotal.toLocaleString('en-IN', {minimumFractionDigits: 0})}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Cost Breakup (Legacy) */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-base font-semibold">Category-wise Cost Breakup <span className="text-slate-400 font-normal text-sm">(Simple format - optional if using items above)</span></Label>
                      <Button type="button" variant="outline" size="sm" onClick={addCostItem}>
                        <Plus className="h-3 w-3 mr-1" />Add Category
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {formData.cost_breakup.map((item, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-3">
                            <Input 
                              value={item.category} 
                              onChange={(e) => updateCostItem(idx, 'category', e.target.value)}
                              placeholder="Work Category"
                            />
                          </div>
                          <div className="col-span-5">
                            <Input 
                              value={item.description} 
                              onChange={(e) => updateCostItem(idx, 'description', e.target.value)}
                              placeholder="Description"
                            />
                          </div>
                          <div className="col-span-3">
                            <Input 
                              type="number" 
                              value={item.amount || ''} 
                              onChange={(e) => updateCostItem(idx, 'amount', e.target.value)}
                              placeholder="Amount (₹)"
                            />
                          </div>
                          <div className="col-span-1">
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removeCostItem(idx)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Commercial Summary */}
                  <div className="bg-white rounded-lg p-4 border">
                    <Label className="text-base font-semibold mb-3 block">Commercial Summary</Label>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div className="flex items-center gap-2">
                        <Label>GST Rate (%)</Label>
                        <Input 
                          type="number" 
                          value={formData.gst_percent} 
                          onChange={(e) => setFormData({...formData, gst_percent: parseFloat(e.target.value) || 0})}
                          className="w-20"
                          disabled={formData.gst_exempt}
                        />
                        <label className="flex items-center gap-2 text-sm">
                          <input 
                            type="checkbox" 
                            checked={formData.gst_exempt}
                            onChange={(e) => setFormData({...formData, gst_exempt: e.target.checked})}
                          />
                          GST Exempt
                        </label>
                      </div>
                    </div>
                    <div className="space-y-2 text-right">
                      <div className="flex justify-between"><span>Sub Total:</span><span className="font-mono">₹{subtotal.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span></div>
                      <div className="flex justify-between"><span>{formData.gst_exempt ? 'GST (Exempt):' : `GST (${formData.gst_percent}%):`}</span><span className="font-mono">₹{gstAmount.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span></div>
                      <div className="flex justify-between font-bold text-brillien-600 text-lg border-t pt-2"><span>Total Project Cost:</span><span className="font-mono">₹{total.toLocaleString('en-IN', {minimumFractionDigits: 2})}</span></div>
                    </div>
                  </div>

                  {/* Payment Milestones */}
                  <div>
                    <Label className="text-base font-semibold mb-3 block">Payment Milestones</Label>
                    <div className="space-y-2">
                      {formData.payment_milestones.map((milestone, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-6">
                            <Input 
                              value={milestone.name} 
                              onChange={(e) => updateMilestone(idx, 'name', e.target.value)}
                              placeholder="Milestone Name"
                            />
                          </div>
                          <div className="col-span-2">
                            <Input 
                              type="number" 
                              value={milestone.percentage} 
                              onChange={(e) => updateMilestone(idx, 'percentage', e.target.value)}
                              placeholder="%"
                            />
                          </div>
                          <div className="col-span-4 text-right font-mono text-sm text-slate-600">
                            ₹{(total * milestone.percentage / 100).toLocaleString('en-IN', {minimumFractionDigits: 2})}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Timelines */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Project Start (days from work order)</Label>
                      <Input 
                        type="number" 
                        value={formData.project_start_days} 
                        onChange={(e) => setFormData({...formData, project_start_days: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <Label>Execution Duration (days)</Label>
                      <Input 
                        type="number" 
                        value={formData.execution_duration_days} 
                        onChange={(e) => setFormData({...formData, execution_duration_days: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <Label>Quotation Validity (days)</Label>
                      <Input 
                        type="number" 
                        value={formData.validity_days} 
                        onChange={(e) => setFormData({...formData, validity_days: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  </div>

                  {/* Exclusions */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-base font-semibold">Exclusions</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addExclusionItem}>
                        <Plus className="h-3 w-3 mr-1" />Add Exclusion
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {formData.exclusions.map((item, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <span className="text-slate-400 text-sm w-6">•</span>
                          <Input 
                            value={item} 
                            onChange={(e) => updateExclusionItem(idx, e.target.value)}
                            placeholder="Enter exclusion item..."
                            className="flex-1"
                          />
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeExclusionItem(idx)}
                            className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Terms & Conditions */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-base font-semibold">Terms & Conditions</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addTermItem}>
                        <Plus className="h-3 w-3 mr-1" />Add Term
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {formData.terms_conditions.map((item, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <span className="text-slate-400 text-sm w-6">•</span>
                          <Input 
                            value={item} 
                            onChange={(e) => updateTermItem(idx, e.target.value)}
                            placeholder="Enter term or condition..."
                            className="flex-1"
                          />
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeTermItem(idx)}
                            className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <Label>Additional Notes</Label>
                    <Textarea 
                      value={formData.notes} 
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={2}
                      placeholder="Any additional notes for this quotation..."
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button 
                      type="submit" 
                      disabled={submitting} 
                      className="bg-brillien-600 hover:bg-brillien-700"
                      data-testid="submit-quotation-btn"
                    >
                      {submitting ? "Creating..." : "Create Quotation"}
                    </Button>
                    <Button type="button" variant="outline" onClick={handleCancelForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            )}

            {/* Quotations List */}
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading quotations...</div>
            ) : projectQuotations.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No quotations created yet</p>
                <p className="text-sm">Click &quot;Create Quotation&quot; to generate a new one</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Quotation No.</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead className="text-right">Total Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projectQuotations.map(qt => (
                    <TableRow key={qt.id} data-testid={`quotation-row-${qt.id}`}>
                      <TableCell className="font-mono text-sm">{qt.quotation_number}</TableCell>
                      <TableCell>{qt.client_name}</TableCell>
                      <TableCell>{qt.project_name}</TableCell>
                      <TableCell className="text-right font-mono">₹{qt.total_amount?.toLocaleString('en-IN', {minimumFractionDigits: 2})}</TableCell>
                      <TableCell>
                        {canWrite ? (
                          <Select 
                            value={qt.status || "Draft"} 
                            onValueChange={(val) => handleStatusUpdate(qt.id, val)}
                          >
                            <SelectTrigger className={`w-32 h-8 ${statusColors[qt.status || 'Draft']}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {statusOptions.map(status => (
                                <SelectItem key={status} value={status}>{status}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={statusColors[qt.status || 'Draft']}>{qt.status || 'Draft'}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{qt.created_at?.substring(0, 10)}</TableCell>
                      <TableCell className="text-sm text-slate-600">{qt.updated_at?.substring(0, 10) || "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {canWrite && qt.status !== "Approved" && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleEditQuotation(qt)}
                              className="text-blue-600 hover:text-blue-700"
                              data-testid={`edit-quotation-${qt.id}`}
                              title="Edit Quotation"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDownloadPDF(qt.id)}
                            className="text-brillien-600 hover:text-brillien-700"
                            data-testid={`download-pdf-${qt.id}`}
                            title="Download PDF"
                            disabled={downloadingPdf === qt.id}
                          >
                            {downloadingPdf === qt.id ? (
                              <span className="h-4 w-4 border-2 border-brillien-600 border-t-transparent rounded-full animate-spin"></span>
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                          {canWrite && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteQuotation(qt.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="boq">
          <BOQTemplateContent projectId={projectId} onRefresh={onRefresh} canWrite={canWrite} token={token} />
        </TabsContent>

        <TabsContent value="variations">
          <VariationsContent projectId={projectId} canWrite={canWrite} token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ NEW BOQ TEMPLATE CONTENT ============
function BOQTemplateContent({ projectId, onRefresh, canWrite, token }) {
  const [boqList, setBoqList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBoq, setEditingBoq] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  
  const boqFormDefaults = {
    title: "Bill of Quantities",
    items: [{ item_name: "", image_url: "", unit: "", quantity: "" }],
    notes: "",
    manual_id: ""  // Optional manual ID
  };
  const [formData, setFormData, draft] = useFormDraft(
    `newBOQ:${projectId}`,
    boqFormDefaults,
    { enabled: showForm && !editingBoq }
  );
  const [draftRestored, setDraftRestored] = useState(false);

  const fetchBoqs = async () => {
    try {
      const res = await axios.get(`${API}/boq/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoqList(res.data);
    } catch (error) {
      toast.error("Failed to fetch BOQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBoqs();
  }, [projectId]);

  const resetForm = () => {
    setFormData(boqFormDefaults);
    setEditingBoq(null);
    setShowForm(false);
    setDraftRestored(false);
  };

  const openNewBoqForm = () => {
    const restored = draft.restoreIfAny();
    setDraftRestored(restored);
    setShowForm(true);
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { item_name: "", image_url: "", unit: "", quantity: "" }]
    });
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== index)
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    
    setUploadingImage(index);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    try {
      const res = await axios.post(`${API}/boq/upload-image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      handleItemChange(index, "image_url", res.data.image_url);
      toast.success("Image uploaded");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(null);
    }
  };

  const handleEditBoq = async (boq) => {
    try {
      const res = await axios.get(`${API}/boq/${boq.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      setFormData({
        title: data.title || "Bill of Quantities",
        items: data.items.map(item => ({
          item_name: item.item_name || "",
          image_url: item.image_url || "",
          unit: item.unit || "",
          quantity: item.quantity?.toString() || ""
        })),
        notes: data.notes || ""
      });
      setEditingBoq(boq);
      setShowForm(true);
    } catch (error) {
      toast.error("Failed to load BOQ");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate items
    const validItems = formData.items.filter(item => item.item_name.trim());
    if (validItems.length === 0) {
      toast.error("Please add at least one item");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        project_id: projectId,
        title: formData.title,
        items: validItems.map(item => ({
          item_name: item.item_name,
          image_url: item.image_url || null,
          unit: item.unit,
          quantity: parseFloat(item.quantity) || 0
        })),
        notes: formData.notes,
        manual_id: formData.manual_id || null  // Include manual ID
      };
      
      if (editingBoq) {
        await axios.put(`${API}/boq/${editingBoq.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("BOQ updated successfully");
      } else {
        await axios.post(`${API}/boq`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("BOQ created successfully");
        draft.clear();
      }
      
      resetForm();
      fetchBoqs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save BOQ");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (boqId) => {
    if (!window.confirm("Are you sure you want to delete this BOQ?")) return;
    try {
      await axios.delete(`${API}/boq/${boqId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("BOQ deleted");
      fetchBoqs();
    } catch (error) {
      toast.error("Failed to delete BOQ");
    }
  };

  const handleDownloadPdf = async (boqId) => {
    try {
      const res = await axios.get(`${API}/boq/${boqId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BOQ_${boqId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  if (loading) return <div className="text-center py-8 text-slate-500">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-slate-900">Bill of Quantities</h4>
        {canWrite && !showForm && (
          <div className="flex items-center gap-3">
            <DraftIndicatorBadge
              storageKey={`newBOQ:${projectId}`}
              onResume={openNewBoqForm}
            />
            <Button 
              onClick={openNewBoqForm} 
              className="bg-brillien-600 hover:bg-brillien-700"
              data-testid="create-boq-btn"
            >
              <Plus className="h-4 w-4 mr-2" />Create BOQ
            </Button>
          </div>
        )}
      </div>

      {/* BOQ Form */}
      {showForm && (
        <Card className="p-6 border-brillien-200 bg-brillien-50/50">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-900 text-lg">
              {editingBoq ? `Edit BOQ - ${editingBoq.id}` : "Create New BOQ"}
            </h4>
            <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!editingBoq && draftRestored && (
              <DraftRestoredBanner
                onDiscard={() => {
                  draft.clear();
                  setFormData(boqFormDefaults);
                  setDraftRestored(false);
                }}
              />
            )}
            {/* Manual ID field - only show when creating new BOQ */}
            {!editingBoq && (
              <div className="bg-slate-50 dark:bg-slate-700/50 p-3 rounded-lg border border-dashed border-slate-300 dark:border-slate-400">
                <Label className="text-sm font-medium">
                  BOQ ID <span className="text-slate-400 font-normal">(Optional - leave blank for auto-generated ID)</span>
                </Label>
                <Input
                  value={formData.manual_id}
                  onChange={(e) => setFormData({ ...formData, manual_id: e.target.value })}
                  placeholder="e.g., BOQ-CUSTOM-001"
                  className="mt-1"
                  data-testid="boq-manual-id"
                />
              </div>
            )}

            {/* Title */}
            <div className="w-full md:w-1/2">
              <Label>BOQ Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Bill of Quantities"
                data-testid="boq-title-input"
              />
            </div>

            {/* Items Table */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base">Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={handleAddItem}>
                  <Plus className="h-4 w-4 mr-1" />Add Item
                </Button>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-brillien-600">
                      <TableHead className="text-white font-semibold w-16 text-center">S.NO</TableHead>
                      <TableHead className="text-white font-semibold">ITEMS</TableHead>
                      <TableHead className="text-white font-semibold w-32 text-center">PICTURE</TableHead>
                      <TableHead className="text-white font-semibold w-24 text-center">UNIT</TableHead>
                      <TableHead className="text-white font-semibold w-28 text-center">QUANTITY</TableHead>
                      <TableHead className="text-white font-semibold w-16 text-center"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.items.map((item, index) => (
                      <TableRow key={index} className={index % 2 === 0 ? "bg-white" : "bg-red-50"}>
                        <TableCell className="text-center font-mono text-sm">{index + 1}</TableCell>
                        <TableCell>
                          <Input
                            value={item.item_name}
                            onChange={(e) => handleItemChange(index, "item_name", e.target.value)}
                            placeholder="Enter item name"
                            className="border-slate-300"
                            data-testid={`item-name-${index}`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          {item.image_url ? (
                            <div className="relative inline-block">
                              <img 
                                src={`${BACKEND_URL}${item.image_url}`} 
                                alt="Item" 
                                className="w-12 h-12 object-cover rounded border"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24' fill='none' stroke='%23cbd5e1' stroke-width='2'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'%3E%3C/circle%3E%3Cpolyline points='21 15 16 10 5 21'%3E%3C/polyline%3E%3C/svg%3E";
                                }}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute -top-2 -right-2 h-5 w-5 p-0 bg-red-500 text-white rounded-full hover:bg-red-600"
                                onClick={() => handleItemChange(index, "image_url", "")}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <label className="cursor-pointer">
                              <div className="w-12 h-12 mx-auto border-2 border-dashed border-slate-300 rounded flex items-center justify-center hover:border-brillien-500 transition-colors">
                                {uploadingImage === index ? (
                                  <span className="text-xs text-slate-400">...</span>
                                ) : (
                                  <Camera className="h-5 w-5 text-slate-400" />
                                )}
                              </div>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(index, e.target.files[0])}
                                disabled={uploadingImage !== null}
                              />
                            </label>
                          )}
                        </TableCell>
                        <TableCell>
                          <Input
                            value={item.unit}
                            onChange={(e) => handleItemChange(index, "unit", e.target.value)}
                            placeholder="e.g., nr, kg"
                            className="border-slate-300 text-center"
                            data-testid={`item-unit-${index}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                            placeholder="0"
                            className="border-slate-300 text-center"
                            data-testid={`item-qty-${index}`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          {formData.items.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveItem(index)}
                              className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">
                {submitting ? "Saving..." : editingBoq ? "Update BOQ" : "Create BOQ"}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {/* BOQ List */}
      {boqList.length === 0 && !showForm ? (
        <div className="text-center py-12 text-slate-500">
          <ClipboardList className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>No BOQs created yet</p>
          {canWrite && <p className="text-sm mt-1">Click "Create BOQ" to add one</p>}
        </div>
      ) : !showForm && (
        <div className="space-y-4">
          {boqList.map((boq) => (
            <Card key={boq.id} className="border-slate-200" data-testid={`boq-card-${boq.id}`}>
              <div className="p-4 flex items-center justify-between border-b border-slate-100">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-brillien-600">{boq.id}</span>
                    <Badge className={statusColors[boq.status] || "bg-slate-100"}>{boq.status}</Badge>
                  </div>
                  <h4 className="font-semibold text-slate-900 mt-1">{boq.title}</h4>
                  <p className="text-xs text-slate-500 mt-1">
                    Created: {formatDate(boq.created_at)} | Updated: {formatDate(boq.updated_at)}
                  </p>
                </div>
                <div className="flex gap-2">
                  {canWrite && (boq.status === "draft" || boq.status === "Draft") && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleEditBoq(boq)}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid={`edit-boq-${boq.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />Edit
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDownloadPdf(boq.id)}
                    className="text-brillien-600 border-brillien-200 hover:bg-brillien-50"
                    data-testid={`download-boq-${boq.id}`}
                  >
                    <Download className="h-4 w-4 mr-1" />Download PDF
                  </Button>
                  {canWrite && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(boq.id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Items Preview */}
              <div className="p-4">
                <p className="text-sm text-slate-600 mb-2">{boq.items?.length || 0} items</p>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-semibold w-16 text-center">S.NO</TableHead>
                        <TableHead className="font-semibold">ITEMS</TableHead>
                        <TableHead className="font-semibold w-20 text-center">PICTURE</TableHead>
                        <TableHead className="font-semibold w-20 text-center">UNIT</TableHead>
                        <TableHead className="font-semibold w-24 text-center">QUANTITY</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {boq.items?.slice(0, 5).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-center font-mono text-sm">{item.s_no || idx + 1}</TableCell>
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell className="text-center">
                            {item.image_url ? (
                              <img 
                                src={`${BACKEND_URL}${item.image_url}`} 
                                alt="Item" 
                                className="w-10 h-10 object-cover rounded mx-auto border"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='%23cbd5e1' stroke-width='2'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'%3E%3C/circle%3E%3Cpolyline points='21 15 16 10 5 21'%3E%3C/polyline%3E%3C/svg%3E";
                                }}
                              />
                            ) : "-"}
                          </TableCell>
                          <TableCell className="text-center">{item.unit}</TableCell>
                          <TableCell className="text-center font-mono">{item.quantity}</TableCell>
                        </TableRow>
                      ))}
                      {boq.items?.length > 5 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-slate-500 text-sm">
                            ... and {boq.items.length - 5} more items
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// VariationsContent - imported from @/components/project-detail/VariationsContent


export { BOQContent, BOQTemplateContent };
